import React, { memo, useCallback } from 'react';

import PropTypes from 'prop-types';
import { TextField, InputAdornment } from '@picsio/ui';

import localization from '../../shared/strings';
import ReadOnlyFieldTooltip from './helpers/ReadOnlyFieldTooltip';
import { useLocalFieldValue, useBlurCommit, useEnterToBlur } from './hooks';

const dollarSign = <InputAdornment>$</InputAdornment>;

const validateNumeric = (v) => (
  v !== '' && Number.isNaN(Number(v))
    ? { isValid: false, errorMessage: localization.PRODUCT_INFO.validation.enterValidNumber }
    : { isValid: true }
);

const PricingField = ({
  value, label, readOnly, readOnlyTooltip, onCommit,
}) => {
  const { localValue, setLocalValue } = useLocalFieldValue(value);

  const commit = useCallback((v) => onCommit(Number(v)), [onCommit]);

  const { handleBlur, error, clearErrorIfValid } = useBlurCommit({
    value,
    localValue,
    setLocalValue,
    onCommit: commit,
    serialize: String,
    validate: validateNumeric,
  });

  const handleChange = useCallback((e) => {
    if (readOnly) return;
    setLocalValue(e.target.value);
    clearErrorIfValid(e.target.value);
  }, [readOnly, setLocalValue, clearErrorIfValid]);

  const save = useCallback(() => {
    if (readOnly) return;
    handleBlur();
  }, [readOnly, handleBlur]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ReadOnlyFieldTooltip tooltip={readOnly ? readOnlyTooltip : null}>
      <TextField
        type="number"
        InputProps={{
          startAdornment: dollarSign,
          placeholder: '0.00',
          inputProps: { min: 0, step: 'any' },
        }}
        value={localValue}
        label={label}
        disabled={readOnly}
        error={Boolean(error)}
        helperText={error}
        onChange={handleChange}
        onBlur={save}
        onKeyPress={handleKeyPress}
      />
    </ReadOnlyFieldTooltip>
  );
};

PricingField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.node.isRequired,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  onCommit: PropTypes.func.isRequired,
};

PricingField.defaultProps = {
  value: '',
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(PricingField);
